import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom"
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import lib, { myRequest } from '../../assets/utils/lib';
import './plans-dashboard.css';
import CurrencyFormat from 'react-currency-format';
import TiersManagement from './TiersManagement';
import AdminCheckinTypesList from '../../components/CheckinTypes/AdminCheckinTypesList';
import AdminCareProgramsList from '../../components/CarePrograms/AdminCareProgramsList';
import DashboardHero from '../dashboard/components/DashboardHero';
import DashboardSection from '../dashboard/components/DashboardSection';
import { TableSkeleton } from '../dashboard/components/DashboardSkeleton';
import { ADMIN_REVIEW_MODAL_CLASS } from '../dashboard/utils';
import PlanListingCard from './PlanListingCard';
import PlanCatalogPanel from './PlanCatalogPanel';
import { PLAN_MONEY_INPUT_PROPS } from './planCatalogHelpers';
import PlanRequestsPanel from '../orgs/components/PlanRequestsPanel';
import usePendingPlanRequestsCount from '../orgs/hooks/usePendingPlanRequestsCount';
import { canManagePlanRequests } from '../orgs/planRequestHelpers';
import EnrollmentSegmentsSettings from '../../components/Settings/EnrollmentSegmentsSettings';

import { Table, Modal, Form, Input, Button, Select, notification, Col, Row, Divider, Space, InputNumber, Empty, Segmented, Tag, Spin, Badge } from 'antd';
import { PlusOutlined, MinusCircleOutlined, LoadingOutlined, CloseOutlined, Loading3QuartersOutlined, CloseCircleFilled, CheckCircleFilled, WarningOutlined, AppstoreOutlined, TableOutlined, SearchOutlined, FileProtectOutlined, TeamOutlined, UnorderedListOutlined, MedicineBoxOutlined, ApartmentOutlined, BankOutlined, UserOutlined, DollarOutlined } from '@ant-design/icons';
import { ProgressBar } from "react-loader-spinner";
import { keysHaveEmptyValues } from "../../assets/utils/Helpers";

const { TextArea } = Input;
const { Option } = Select;

const MODAL_PROPS = {
  rootClassName: ADMIN_REVIEW_MODAL_CLASS,
  className: `${ADMIN_REVIEW_MODAL_CLASS}__dialog`,
};

function Plans() {
  const { user } = useAuth();
  const { hasAnyPermission, canStayOnModule } = usePermissions();
  const navigator = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'catalog';
  const [createPlanOpen, setCreatePlanOpen] = useState(false);

  const canReviewPlanRequests = useMemo(
    () => canManagePlanRequests(user, hasAnyPermission),
    [user, hasAnyPermission],
  );
  const { count: pendingPlanRequestsCount } = usePendingPlanRequestsCount(
    Boolean(user && canReviewPlanRequests),
  );

  const filterOrgId = searchParams.get('org_id') ? Number(searchParams.get('org_id')) : null;
  const filterOrgLabel = searchParams.get('org_name') || '';

  const handleTabChange = (key) => {
    if (key === 'catalog') {
      setSearchParams({});
    } else if (key === 'plan-requests') {
      setSearchParams({ tab: 'plan-requests' });
    } else if (key === 'enrollment-segments') {
      setSearchParams({ tab: 'enrollment-segments' });
    } else {
      setSearchParams({ tab: key });
    }
  };

  const tabOptions = useMemo(() => [
    { label: 'Catalog', value: 'catalog' },
    { label: 'Designer', value: 'plans' },
    { label: 'Segment rules', value: 'enrollment-segments' },
    ...(canReviewPlanRequests ? [{
      label: (
        <span className={`dash-tab-label${pendingPlanRequestsCount > 0 ? ' dash-tab-label--attention' : ''}`}>
          <span>Plan requests</span>
          {pendingPlanRequestsCount > 0 ? (
            <Badge
              count={pendingPlanRequestsCount}
              size="small"
              overflowCount={99}
              className="dash-tab-badge"
            />
          ) : null}
        </span>
      ),
      value: 'plan-requests',
    }] : []),
    { label: 'Tiers', value: 'tiers' },
    { label: 'Categories', value: 'categories' },
    { label: 'Benefit Items', value: 'items' },
    { label: 'Encounter Types', value: 'encounter-types' },
    { label: 'Care Programs', value: 'care-programs' },
  ], [canReviewPlanRequests, pendingPlanRequestsCount]);

  useEffect(() => {
    if (!user) {
      navigator('/login');
      return;
    }
    if (!canStayOnModule('plans', [1, 5])) {
      notification.warning({
        message: 'Access Denied',
        description: "You don't have permission to view plans.",
        placement: 'topLeft'
      });
      navigator('/dashboard');
    }
  }, [user, canStayOnModule, navigator]);

  const tabBar = (
    <Segmented
      value={activeTab}
      onChange={handleTabChange}
      options={tabOptions}
    />
  );

  return (
    <div className="plans-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">Plans Management</h1>
            <p className="dash-masthead__meta">
              Catalog segments & pricing, benefit design (categories/items/limits), segment rules, and care programs
            </p>
          </div>
        </header>

        <PlansSummary />

        <div className="dash-tab-strip" role="tablist" aria-label="Plans sections">
          {tabBar}
        </div>

        <div className="plans-tab-panel">
          {activeTab === 'catalog' ? (
            <PlanCatalogPanel onCreatePlan={() => setCreatePlanOpen(true)} />
          ) : null}
          {activeTab === 'plans' ? <CreatePlan /> : null}
          {createPlanOpen ? (
            <CreatePlan
              listingMode="modal-only"
              forceCreateOpen={createPlanOpen}
              onCreateOpenChange={setCreatePlanOpen}
            />
          ) : null}
          {activeTab === 'enrollment-segments' ? <EnrollmentSegmentsSettings embedded /> : null}
          {activeTab === 'plan-requests' && canReviewPlanRequests ? (
            <PlanRequestsPanel
              showOrgFilter
              filterOrgId={filterOrgId}
              filterOrgLabel={filterOrgLabel}
              onOrgFilterChange={(org) => {
                if (org?.org_id) {
                  setSearchParams({
                    tab: 'plan-requests',
                    org_id: String(org.org_id),
                    org_name: org.org_name || '',
                  }, { replace: true });
                } else {
                  setSearchParams({ tab: 'plan-requests' }, { replace: true });
                }
              }}
            />
          ) : null}
          {activeTab === 'tiers' ? <TiersManagement /> : null}
          {activeTab === 'categories' ? <CreateCategory /> : null}
          {activeTab === 'items' ? <CreateItems /> : null}
          {activeTab === 'encounter-types' ? <AdminCheckinTypesList embedded /> : null}
          {activeTab === 'care-programs' ? <AdminCareProgramsList embedded /> : null}
        </div>
      </div>
    </div>
  );
}

export const PlansSummary = () => {
  const [summary, setSummary] = useState({})
  const [loading, setLoading] = useState(false)

  const getSummary = (first = false) => {
    if (first) setLoading(true);
    lib.plansSummary()
      .then((resp) => resp.data)
      .then((planTypes) => {
        if (planTypes && !planTypes?.error) setSummary(planTypes?.data)
      })
      .finally(() => { setLoading(false) })
  }

  useEffect(() => {
    getSummary(true)
    const timerId = setInterval(getSummary, 5000);
    return () => clearInterval(timerId);
  }, []);

  const heroMetrics = useMemo(() => {
    if (!Object.keys(summary).length) return [];

    return [
      {
        key: 'plans',
        title: 'Plans',
        value: Number(summary?.plans) || 0,
        color: '#3497D3',
        icon: <FileProtectOutlined />,
        subtitle: 'Active catalog',
      },
      {
        key: 'categories',
        title: 'Categories',
        value: Number(summary?.categories) || 0,
        color: '#722ed1',
        icon: <MedicineBoxOutlined />,
        subtitle: 'Benefit groupings',
      },
      {
        key: 'items',
        title: 'Benefits',
        value: Number(summary?.items) || 0,
        color: '#49B870',
        icon: <UnorderedListOutlined />,
        subtitle: 'Benefit items',
      },
      {
        key: 'organizations',
        title: 'Organizations',
        value: Number(summary?.organizations) || 0,
        color: '#0ea5e9',
        icon: <BankOutlined />,
        subtitle: 'Corporate accounts',
      },
      {
        key: 'selfPaidPolicies',
        title: 'Self-paid',
        value: Number(summary?.selfPaidPolicies) || 0,
        color: '#6366f1',
        icon: <UserOutlined />,
        subtitle: 'Individual policies',
      },
      {
        key: 'corporatePolicies',
        title: 'Corporate',
        value: Number(summary?.corporatePolicies) || 0,
        color: '#8b5cf6',
        icon: <ApartmentOutlined />,
        subtitle: 'Organization policies',
      },
      {
        key: 'totalPolicies',
        title: 'Policies',
        value: Number(summary?.totalPolicies) || 0,
        color: '#3497D3',
        icon: <TeamOutlined />,
        subtitle: 'All policy types',
      },
      {
        key: 'enrolees',
        title: 'Enrollees',
        value: Number(summary?.enrolees) || 0,
        color: '#49B870',
        icon: <UnorderedListOutlined />,
        subtitle: 'Across all policies',
      },
      {
        key: 'totalPolicyCost',
        title: 'Policy cost',
        value: Number(summary?.totalPolicyCost) || 0,
        color: '#faad14',
        icon: <DollarOutlined />,
        subtitle: 'Portfolio value',
        prefix: '₦',
        compact: true,
      },
    ];
  }, [summary]);

  if (!loading && !heroMetrics.length) {
    return (
      <section className="plans-overview dash-overview dash-overview--compact dash-overview--dense" aria-label="Plans summary">
        <Empty description="Summary is not available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </section>
    );
  }

  return (
    <section className="plans-overview dash-overview dash-overview--compact dash-overview--dense" aria-label="Plans summary">
      <DashboardHero metrics={heroMetrics} loading={loading} />
    </section>
  );
}

const CreatePlan = ({
  listingMode = 'full',
  forceCreateOpen = false,
  onCreateOpenChange,
} = {}) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [categoriesDesign, setCategoriesDesign] = useState([]);

  const [summaries, setSummaries] = useState([]);

  const planFields = ['plan_name', 'plan_description', 'plan_type', 'tier_id'];

  const [visiblePlan, setVisiblePlan] = useState(false);
  const [visiblePlanDetails, setVisiblePlanDetails] = useState(false);
  const [visiblePlanEdit, setVisiblePlanEdit] = useState(false);
  const [visiblePlanDesign, setVisiblePlanDesign] = useState(false);

  const [visiblePlanSummary, setVisiblePlanSummary] = useState(false);

  const [PlanToEdit, setPlanToEdit] = useState({});
  const [PlanToDesign,] = useState({});
  const [PlanDetails,] = useState({});
  const [formCreatePlans, setFormCreatePlans] = useState({});

  const [PlanToSummary,] = useState({});

  const [planTypes, setPlanTypes] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingDetails,] = useState(false);
  const [loadingSummaries, setLoadingSummaries] = useState(false);
  const [planSearch, setPlanSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [view, setView] = useState('list');

  const closeCreateModal = () => {
    setVisiblePlan(false);
    if (typeof onCreateOpenChange === 'function') onCreateOpenChange(false);
  };

  useEffect(() => {
    if (forceCreateOpen) setVisiblePlan(true);
  }, [forceCreateOpen]);

  const getPlanTypes = async () => {
    lib.getPlanTypes()
      .then((resp) => resp.data)
      .then((types) => types?.data?.map(planType => { return { value: planType.plans_types_id, label: planType.plans_types_name } }))
      .then(render => setPlanTypes((render)))
  }

  const getPlans = () => {
    lib.getAdminPlansCatalog({ scope: 'all' })
      .then((resp) => resp?.data)
      .then((payload) => {
        const v2Plans = payload?.data?.plans;
        if (Array.isArray(v2Plans)) {
          setPlans(v2Plans);
          return;
        }
        return lib.getPlans()
          .then((resp) => resp.data)
          .then((legacyPlans) => legacyPlans?.data)
          .then((render) => setPlans(Array.isArray(render) ? render : []));
      })
      .catch(() => {
        lib.getPlans()
          .then((resp) => resp.data)
          .then((legacyPlans) => legacyPlans?.data)
          .then((render) => setPlans(Array.isArray(render) ? render : []));
      });
  }

  const getActiveTiers = () => {
    lib.getAdminTiers({ scope: 'active' })
      .then((resp) => resp?.data)
      .then((payload) => {
        const list = Array.isArray(payload?.data?.tiers) ? payload.data.tiers : [];
        const options = list
          .sort((a, b) => Number(a?.tier_rank || 0) - Number(b?.tier_rank || 0))
          .map((tier) => ({
            value: Number(tier?.tier_id),
            label: `${tier?.tier_name} (Rank: ${tier?.tier_rank})`,
            raw: tier,
          }));
        setTiers(options);
      })
      .catch(() => setTiers([]));
  }

  const getCategoriesDesign = () => {
    myRequest('post', 'categoriesDesign', {})
      .then((resp) => resp.data)
      .then((planTypes) => planTypes?.data)
      .then(render => setCategoriesDesign(render))
  }

  const updatePlan = (data = false) => {
    console.log(data || PlanToEdit)
    setLoading(true)
    lib.updatePlan(data || PlanToEdit)
      .then((resp) => resp.data)
      .then((plan) => {
        if (plan?.status === 'error') {
          notification.error({ message: plan.msg, placement: 'topLeft' })
        } else if (Number(plan?.error) === 0) {
          notification.success({ message: plan.message, placement: 'topLeft' })
          setVisiblePlanEdit(false)
        } else {
          notification.error({ message: plan.message, placement: 'topLeft' })
          // getPlans();
        }
        // planTypes?.data
        console.log(plan)
        getPlans()
      })
      .finally(() => setLoading(false))
  }

  const getSummary = () => {
    setLoadingSummaries(true);
    myRequest('post', 'planSummary', PlanToSummary)
      .then((resp) => resp?.data)
      .then((detail) => setSummaries(detail?.data ?? []))
      .finally(() => setLoadingSummaries(false))
  }

  useEffect(() => {
    getPlans();
    getActiveTiers();
  }, [])

  useEffect(() => {
    if (visiblePlan) getPlanTypes();
    if (visiblePlan) getActiveTiers();
    else setFormCreatePlans({});
  }, [visiblePlan])

  useEffect(() => {
    if (visiblePlanEdit) getPlanTypes();
    if (visiblePlanEdit) getActiveTiers();
    else setPlanToEdit({});
  }, [visiblePlanEdit])

  useEffect(() => {
    // console.log(formCreatePlans);
  }, [formCreatePlans])

  useEffect(() => {
    visiblePlanDesign && getCategoriesDesign();
    form.setFieldValue('plan', PlanToDesign.plan_id)
  }, [visiblePlanDesign]) //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    visiblePlanSummary && getSummary();
  }, [visiblePlanSummary]) //eslint-disable-line react-hooks/exhaustive-deps

  const filteredPlans = useMemo(() => {
    const normalizedSearch = planSearch.trim().toLowerCase();
    return (Array.isArray(plans) ? plans : []).filter((plan) => {
      const planStatus = Number(plan?.plan_status) ? 'active' : 'inactive';
      const statusMatches = statusFilter === 'all' || planStatus === statusFilter;
      const tierId = plan?.tier_id != null ? String(plan?.tier_id) : 'unassigned';
      const tierMatches = tierFilter === 'all' || tierId === tierFilter;
      const searchable = [
        plan?.plan_name,
        plan?.plan_description,
        plan?.plans_types_name,
        plan?.plan_type_name,
        plan?.tier_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const searchMatches = !normalizedSearch || searchable.includes(normalizedSearch);
      return statusMatches && tierMatches && searchMatches;
    });
  }, [planSearch, plans, statusFilter, tierFilter]);

  const createPlan = () => {
    if (keysHaveEmptyValues(formCreatePlans, planFields)) {
      notification.warning({ message: 'There are some empty fields, kindly fill then and try again.', placement: 'topLeft' })
      return;
    }
    if (!formCreatePlans?.tier_id) {
      notification.warning({ message: 'Tier is required for new plans.', placement: 'topLeft' })
      return;
    }
    setLoading(true)
    lib.createPlan(formCreatePlans)
      .then((resp) => resp.data)
      .then((planData) => {
        if (Number(planData?.error) === 0) {
          notification.success({ message: planData.message, placement: 'topLeft' })
          closeCreateModal()
        } else {
          notification.error({ message: planData.message, placement: 'topLeft' })
        }
        console.log(planData)
        getPlans();

      }).finally(() => setLoading(false))
  }

  const [form] = Form.useForm();

  const [formSummary] = Form.useForm();

  const onFinish = (values) => {
    console.log('Received values of form:', values);
  };

  const handleChange = (e) => {
    form.setFieldsValue({
      categories: [],
    });
  };

  const handleItemSelection = e => {
    // console.log(e, categories.filter(item => item.plans_categories_id != e))
    // setItemsCopy(d => (d.filter(item => item.plans_items_id != e)))
    // console.log(form.getFieldsValue())
  }

  const handleDesignClosed = e => {
    setVisiblePlanDesign(false)
    form.setFieldsValue({
      categories: [],
    });
  }

  const handleDesignSubmit = e => {
    form.validateFields().then(values => {
      setVisiblePlanDesign(false)
      lib.designplan(form.getFieldsValue())
        .then(res => res.data)
        .then(des => {
          form.setFieldValue('categories', []);
        });
    })
      .catch(e => {
        notification.error({ message: 'Form Fields Error', placement: 'topLeft' })
      })
  }

  const handlePanSummaryClosed = e => {
    setVisiblePlanSummary(false)
    setSummaries([])
    formSummary.setFieldsValue({
      summaries: [],
    });
  }

  const handlePlanSummarySubmit = e => {
    formSummary.validateFields().then(values => {
      // console.log('value', {...values, ...PlanToSummary});
      if (!values['summaries']?.length) {
        setVisiblePlanSummary(false);
        setSummaries([])
        return;
      }
      setLoadingSummaries(true)
      myRequest('post', 'addPlanSummary', { ...values, ...PlanToSummary })
        .then(response => response.data)
        .then(res => {
          if (res?.error) {
            notification.error({ message: res?.message, placement: 'topLeft' })
          } else {
            notification.success({ message: res?.message, placement: 'topLeft' })
            formSummary.setFieldValue('summaries',)
          }
        })
        .finally(() => {
          getSummary()
          setLoadingSummaries(false)
        })
    })
      .catch(e => {
        notification.error({ message: 'Form Fields Error: Kindly remove empty fields', placement: 'topLeft' })
      })
  }

  const removeSummary = id => {
    setLoadingSummaries(true)
    myRequest('post', 'removePlanSummary', id)
      .then(response => response.data)
      .then(res => {
        if (res?.error) {
          notification.error({ message: res?.message, placement: 'topLeft' })
        } else {
          notification.success({ message: res?.message, placement: 'topLeft' })
        }
      })
      .finally(() => {
        getSummary()
        setLoadingSummaries(false)
      })
  }

  const handleRowClick = (record) => {
    navigate(`/plans/${btoa(record.plan_id)}`, { state: record })
  };

  const rowProps = (record) => {
    return {
      onClick: () => handleRowClick(record)
    };
  };

  const columnConf = [
    {
      title: 'Plan Name',
      key: 'plan_name',
      dataIndex: 'plan_name',
      width: '25%',
      render: (_, record) => {
        const hasTier = record?.tier_id != null && record?.tier_id !== '';
        const tierName = record?.tier_name;
        return (
          <Space direction="vertical" size={2}>
            <span>{record?.plan_name}</span>
            {hasTier ? (
              <Tag color="blue">{tierName || `Tier ${record?.tier_id}`}</Tag>
            ) : (
              <Tag color="orange" icon={<WarningOutlined />}>-- Not assigned --</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Plan Unit Cost',
      key: 'plan_cost',
      dataIndex: 'plan_cost',
      width: '15%',
      render: (_) => (_ == null || _ === ''
        ? '—'
        : <CurrencyFormat value={_} displayType="text" thousandSeparator prefix="₦" />)
    },
    {
      title: 'Plan Family Cost',
      key: 'plan_six_cost',
      dataIndex: 'plan_six_cost',
      width: '15%',
      render: (_) => (_ == null || _ === ''
        ? '—'
        : <CurrencyFormat value={_} displayType="text" thousandSeparator prefix="₦" />)
    },
    // {
    //   title: 'In-Patient Limit',
    //   key: 'in_patient_limit',
    //   dataIndex: 'in_patient_limit',
    //   width: '15%'
    // },
    // {
    //   title: 'Out-Patient Limit',
    //   key: 'out_patient_limit',
    //   dataIndex: 'out_patient_limit',
    //   width: '15%'
    // },
    {
      title: 'Plan Description',
      key: 'plan_description',
      dataIndex: 'plan_description',
      width: '20%'
    },
    {
      title: 'Plan Type',
      key: 'plans_types_name',
      dataIndex: 'plans_types_name',
      width: 'auto',
      render: (_, record) => record?.plans_types_name || record?.plan_type_name || '--'
    },
    {
      title: 'Status',
      key: 'plan_status',
      dataIndex: 'plan_status',
      render: (_) => parseInt(_) ? <CheckCircleFilled style={{ fontSize: 25 }} /> : <CloseCircleFilled style={{ color: "#ef4d0e", fontSize: 25 }} />,
      width: 'auto',
    }
  ]

  const hasActiveFilters = statusFilter !== 'all' || tierFilter !== 'all' || Boolean(planSearch.trim());

  const clearPlanFilters = () => {
    setPlanSearch('');
    setStatusFilter('all');
    setTierFilter('all');
  };

  const createModal = (
    <Modal
      {...MODAL_PROPS}
      open={visiblePlan}
      title="Create New Plans"
      onOk={null}
      onCancel={closeCreateModal}
      footer={[
        <Button type="primary" danger onClick={closeCreateModal} disabled={loading}>Close</Button>,
        <Button type="primary" onClick={createPlan} loading={loading} disabled={loading}>Create</Button>
      ]}
    >
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Row>
            <Col span={8}>Plan Name</Col>
            <Col span={16}>
              <Input type="text" autoFocus style={{ width: '100%' }} value={formCreatePlans?.plan_name} onChange={e => setFormCreatePlans(d => ({ ...d, plan_name: e.target.value }))} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Plan Description</Col>
            <Col span={16}>
              <TextArea type="text" rows={4} style={{ width: '100%' }} value={formCreatePlans?.plan_description} onChange={e => setFormCreatePlans(d => ({ ...d, plan_description: e.target.value }))} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Plan Type</Col>
            <Col span={16}>
              <Select style={{ width: '100%' }} value={formCreatePlans?.plan_type} onChange={e => setFormCreatePlans(d => ({ ...d, plan_type: e }))} options={planTypes} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Tier</Col>
            <Col span={16}>
              <Select
                style={{ width: '100%' }}
                value={formCreatePlans?.tier_id ?? undefined}
                onChange={e => setFormCreatePlans(d => ({ ...d, tier_id: e }))}
                options={tiers}
                placeholder="Select tier"
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Plan Cost</Col>
            <Col span={16}>
              <InputNumber {...PLAN_MONEY_INPUT_PROPS} style={{ width: '100%' }} value={formCreatePlans?.plan_cost} onChange={e => setFormCreatePlans(d => ({ ...d, plan_cost: e }))} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Plan Cost - Family</Col>
            <Col span={16}>
              <InputNumber {...PLAN_MONEY_INPUT_PROPS} style={{ width: '100%' }} value={formCreatePlans?.plan_six_cost} onChange={e => setFormCreatePlans(d => ({ ...d, plan_six_cost: e }))} />
            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>
  );

  if (listingMode === 'modal-only') {
    return createModal;
  }

  return <>
    <DashboardSection
      id="plans-catalog"
      icon={<FileProtectOutlined />}
      title="Plans catalog"
      subtitle={`${filteredPlans.length} of ${(plans || []).length} plan${(plans || []).length === 1 ? '' : 's'}`}
      collapsible={false}
      variant="content"
      action={(
        <div className="plans-toolbar plans-toolbar--inline">
          <Input
            className="plans-toolbar__search"
            size="small"
            placeholder="Search plans…"
            prefix={<SearchOutlined />}
            value={planSearch}
            onChange={(e) => setPlanSearch(e.target.value)}
            allowClear
          />
          <Select
            className="plans-toolbar__select"
            size="small"
            value={statusFilter}
            options={[
              { label: 'All statuses', value: 'all' },
              { label: 'Active', value: 'active' },
              { label: 'Inactive', value: 'inactive' },
            ]}
            onChange={setStatusFilter}
          />
          <Select
            className="plans-toolbar__select"
            size="small"
            value={tierFilter}
            onChange={setTierFilter}
            options={[
              { label: 'All tiers', value: 'all' },
              { label: 'Unassigned', value: 'unassigned' },
              ...tiers.map((tier) => ({ label: tier.label, value: String(tier.value) })),
            ]}
          />
          <Button size="small" onClick={clearPlanFilters} disabled={!hasActiveFilters}>
            Clear
          </Button>
          <Segmented
            size="small"
            value={view}
            onChange={setView}
            options={[
              { label: 'List', value: 'list', icon: <AppstoreOutlined /> },
              { label: 'Table', value: 'table', icon: <TableOutlined /> },
            ]}
          />
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setVisiblePlan(true)}>
            New plan
          </Button>
        </div>
      )}
    >
      {loading && !plans.length ? (
        view === 'table' ? (
          <TableSkeleton rows={8} columns={6} />
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <Spin size="large" />
          </div>
        )
      ) : filteredPlans.length === 0 ? (
        <Empty description="No plans match the current filters" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : view === 'table' ? (
        <div className="plans-table-wrap">
          <Table
            columns={columnConf}
            dataSource={filteredPlans}
            onRow={rowProps}
            rootClassName="row"
            rowKey={(record) => String(record?.plan_id)}
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total) => `${total} plans`,
            }}
          />
        </div>
      ) : (
        <div className="plans-listing-stack">
          {filteredPlans.map((plan) => (
            <PlanListingCard
              key={plan.plan_id}
              record={plan}
              onClick={() => handleRowClick(plan)}
            />
          ))}
        </div>
      )}
    </DashboardSection>

    {createModal}

    {/* Designing Modal */}
    <Modal
      {...MODAL_PROPS}
      open={visiblePlanDesign}
      title="Plan Design"
      onOk={null}
      onCancel={handleDesignClosed}
      footer={[
        <Button type="primary" danger onClick={handleDesignClosed} >Close</Button>,
        <Button type="primary" onClick={handleDesignSubmit} >Save</Button>
      ]}
      style={{ maxHeight: 900 }}
      width={800}
    >
      <Form
        form={form}
        name="dynamic_form_complex"
        onFinish={onFinish}
        autoComplete="off"
      // onValuesChange={(changed, values) => console.log(changed, values)}
      >
        <Form.Item
          name="plan"
          label="Plan"
          rules={[
            {
              required: true,
              message: 'Missing area',
            },
          ]}
        >
          <Select onChange={handleChange} disabled={PlanToDesign?.plan_id}>
            {plans?.map(plan => <Option value={plan.plan_id}>{plan.plan_name}</Option>)}
          </Select>
        </Form.Item>
        <Form.List name="categories">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <Row key={field.key} style={{ width: '100%' }}>
                  <Col span={18}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, curValues) =>
                        prevValues.area !== curValues.area || prevValues.sights !== curValues.sights
                      }
                      style={{
                        width: '70%',
                      }}
                    >
                      {() => (
                        <Form.Item
                          {...field}
                          label="Category"
                          name={[field.name, 'category']}
                          rules={[
                            {
                              required: true,
                              message: 'Missing Category',
                            },
                          ]}
                          style={{
                            width: '100%',
                          }}
                        >
                          <Select
                            disabled={!form.getFieldValue('plan')}
                            onChange={handleItemSelection}
                            style={{
                              width: '100%',
                            }}
                          >
                            {categoriesDesign.map((item) => (
                              <Option key={item.plans_categories_name} value={item.plans_categories_id} disabled={form.getFieldsValue()?.categories?.find(itm => itm?.category === item.plans_categories_id)}>
                                {item.plans_categories_name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Space align="baseline" style={{ width: '100%' }}>
                      <Form.Item
                        {...field}
                        label="Price"
                        name={[field.name, 'price']}
                        rules={[
                          {
                            required: true,
                            message: 'Missing price',
                          },
                        ]}
                        style={{
                          width: '100%',
                        }}
                      >
                        <InputNumber />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </Space>
                  </Col>
                </Row>
              ))}

              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} disabled={!form.getFieldValue('plan')}>
                  Add Category
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>

    {/* Editing Modal */}
    <Modal
      {...MODAL_PROPS}
      open={visiblePlanEdit}
      title="Edit Plan"
      onOk={null}
      onCancel={() => setVisiblePlanEdit(false)}
      footer={[
        <Button type="primary" danger onClick={() => setVisiblePlanEdit(false)} disabled={loading}>Close</Button>,
        <Button type="primary" onClick={() => updatePlan()} loading={loading} disabled={loading}>Save</Button>
      ]}
    >
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Row>
            <Col span={8}>Plan Name</Col>
            <Col span={16}>
              <Input type="text" style={{ width: '100%' }} value={PlanToEdit.plan_name} onChange={e => setPlanToEdit(d => ({ ...d, plan_name: e.target.value }))} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Plan Description</Col>
            <Col span={16}>
              <TextArea type="text" style={{ width: '100%' }} value={PlanToEdit.plan_description} onChange={e => setPlanToEdit(d => ({ ...d, plan_description: e.target.value }))} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Plan Type</Col>
            <Col span={16}>
              <Select style={{ width: '100%' }} value={PlanToEdit?.plan_type} onChange={e => setPlanToEdit(d => ({ ...d, plan_type: e }))} options={planTypes} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Tier</Col>
            <Col span={16}>
              <Select
                style={{ width: '100%' }}
                value={PlanToEdit?.tier_id != null ? Number(PlanToEdit?.tier_id) : undefined}
                onChange={e => setPlanToEdit(d => ({ ...d, tier_id: e }))}
                options={tiers}
                placeholder="-- Not assigned --"
                allowClear
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Plan Cost</Col>
            <Col span={16}>
              <InputNumber {...PLAN_MONEY_INPUT_PROPS} style={{ width: '100%' }} value={PlanToEdit?.plan_cost} onChange={e => setPlanToEdit(d => ({ ...d, plan_cost: e }))} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Plan Cost - Family</Col>
            <Col span={16}>
              <InputNumber {...PLAN_MONEY_INPUT_PROPS} style={{ width: '100%' }} value={PlanToEdit?.plan_six_cost} onChange={e => setPlanToEdit(d => ({ ...d, plan_six_cost: e }))} />
            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>

    {/* Details Modal */}
    <Modal
      {...MODAL_PROPS}
      title={<><strong>{PlanDetails?.plan_name}</strong> - Detail</>}
      centered
      open={visiblePlanDetails}
      onCancel={() => setVisiblePlanDetails(false)}
      footer={[<Button onClick={() => setVisiblePlanDetails(false)}>Ok</Button>]}
      closeIcon={loadingDetails ? <LoadingOutlined /> : <CloseOutlined />}
      width={1000}
      destroyOnClose
    >
      <Row gap={[12, 24]} justify={'center'}>
        <Col span={24}>
          <Row gap={[8, 18]} >
            <Col span={6}><strong>Plan Name:</strong></Col>
            <Col span={18}>{PlanDetails?.plan_name}</Col>
            <Col span={6}><strong>Plan Description:</strong></Col>
            <Col span={18}>{PlanDetails?.plan_description}</Col>
            <Col span={6}><strong>Plan Cost:</strong></Col>
            <Col span={18}>{PlanDetails?.plan_cost} Naira</Col>
          </Row>
        </Col>
        <Divider style={{ padding: 0, margin: '10px 0', fontWeight: 800 }} orientation="left" orientationMargin={0}>
          Plan Categories
        </Divider>
        <Col span={24}>
          <ProgressBar
            height="150"
            width="150"
            ariaLabel="progress-bar-loading"
            wrapperStyle={{ position: 'relative', top: '10%', left: '40%' }}
            wrapperClass="progress-bar-wrapper"
            borderColor='#14442E'
            barColor='#51250F'
            visible={loadingDetails}
          />
          {
            (!loadingDetails) ?
              (PlanDetails?.categories?.length) ? PlanDetails?.categories?.map(category => <Row gap={[12, 12]} style={{ paddingLeft: 20 }} key={category?.plans_categories_items_item_id}>
                <Col span={6}><strong>Category Name:</strong></Col>
                <Col span={18}><em>{category?.plans_categories_name}</em></Col>
                <Col span={6}><strong>Category Description:</strong></Col>
                <Col span={18}><em>{category?.plans_categories_description}</em></Col>
                <Col span={6}><strong>Category Limit:</strong></Col>
                <Col span={18}>
                  <em><CurrencyFormat value={category?.plan_plan_category_cost} displayType={'text'} thousandSeparator={true} prefix={'₦'} /></em>
                </Col>
                <Col span={6}><strong>Category Status:</strong></Col>
                <Col span={18}><em>{parseInt(category?.plans_categories_status) ? 'Active' : 'Disabled'}</em></Col>
                <Divider style={{ padding: 0, margin: '10px 0', marginLeft: 20, fontWeight: 800 }} orientation="left" orientationMargin={0}>
                  Category Items
                </Divider>
                <Col span={24}>
                  {
                    category?.items?.length ? category?.items?.map(item => <Row key={item.plans_items_id} style={{ paddingLeft: 20 }}>
                      <Col span={6}><strong>Item Name:</strong></Col>
                      <Col span={18}><em>{item?.plans_items_name}</em></Col>
                      <Col span={6}><strong>Item Description:</strong></Col>
                      <Col span={18}><em>{item?.plans_items_description}</em></Col>
                      <Col span={6}><strong>Item Limit:</strong></Col>
                      <Col span={18}>
                        <em><CurrencyFormat value={item?.plans_categories_items_cost} displayType={'text'} thousandSeparator={true} prefix={'₦'} /></em>
                      </Col>
                      <Col span={6}><strong>Item Status:</strong></Col>
                      <Col span={18}><em>{parseInt(item?.plans_items_status) ? 'Active' : 'Disabled'}</em></Col>
                      <Col span={24}>&nbsp;</Col>
                    </Row>) : <Empty description={'there are no items for this plan category'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  }
                </Col>
                <Col span={24}>&nbsp;</Col>
              </Row>) :
                <Empty description={'not designed yet...'} image={Empty.PRESENTED_IMAGE_SIMPLE} /> : null
          }
        </Col>
      </Row>
    </Modal>

    {/* Perks Modal */}
    <Modal
      {...MODAL_PROPS}
      open={visiblePlanSummary}
      title={"Plan Perks - " + PlanToSummary?.plan_name}
      onOk={null}
      onCancel={handlePanSummaryClosed}
      footer={[
        <Button type="primary" danger onClick={handlePanSummaryClosed} disabled={loadingSummaries}>Close</Button>,
        <Button type="primary" onClick={handlePlanSummarySubmit} disabled={loadingSummaries} loading={loadingSummaries}>Save</Button>
      ]}
      style={{ maxHeight: 900 }}
      width={800}
    >
      <Form
        form={formSummary}
        name="dynamic_form_complex_2"
        autoComplete="off"
      // onValuesChange={(changed, values) => console.log(changed, values)}
      >
        {
          loadingSummaries && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loading3QuartersOutlined spin style={{ fontSize: 50 }} />
          </div>
        }
        {summaries?.map((su, index) => (
          <Row key={index} style={{ width: '100%' }} gutter={[12, 12]}>
            <Col span={21}>
              <strong>
                {su?.plan_summary_text}
              </strong>
            </Col>
            <Col span={3}>
              <Space align="baseline" style={{ width: '100%' }}>
                <Button type="dashed" danger icone={<MinusCircleOutlined />} onClick={() => removeSummary({ id: su?.plan_summary_id })} disabled={loadingSummaries}>Remove</Button>
              </Space>
            </Col>
          </Row>
        ))}
        <Divider />
        <Form.List name="summaries">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <Row key={field.key} style={{ width: '100%' }} gutter={[12, 4]}>
                  <Col span={23}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, curValues) =>
                        prevValues.area !== curValues.area || prevValues.sights !== curValues.sights
                      }
                      style={{
                        width: '70%',
                      }}
                    >
                      {() => (
                        <Form.Item
                          {...field}
                          // label={`Summary ${++index}`}
                          name={[field.name, 'item']}
                          rules={[
                            {
                              required: true,
                              message: 'Missing Value',
                            },
                          ]}
                          style={{
                            width: '100%',
                          }}
                        >
                          <Input disabled={loadingSummaries} />
                        </Form.Item>
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={1}>
                    <Space align="baseline" style={{ width: '100%' }}>
                      <MinusCircleOutlined onClick={() => remove(field.name)} disabled={loadingSummaries} />
                    </Space>
                  </Col>
                </Row>
              ))}

              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} disabled={loadingSummaries}>
                  Add Summary
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  </>
}

const CreateCategory = () => {
  const [categories, setCategories] = useState([]);
  const [itemsDesign, setItemsDesign] = useState([]);

  const categoryFields = ['plans_categories_name', 'plans_categories_description'];

  const [visibleCategory, setVisibleCategory] = useState(false);
  const [visibleCategoryEdit, setVisibleCategoryEdit] = useState(false);
  const [visibleCategoryDesign, setVisibleCategoryDesign] = useState(false);

  const [CategoryToDesign,] = useState({});
  const [CategoryToEdit, setCategoryToEdit] = useState({});
  const [CategoryToEditCopy, setCategoryToEditCopy] = useState({});
  const [formCreateCategory, setFormCreateCategory] = useState({});

  const [loading, setLoading] = useState(false);

  const [visibleCategoryDetails, setVisibleCategoryDetails] = useState(false);
  const [CategoryDetails,] = useState({});
  const [loadingDetails,] = useState(false);


  const getCategories = () => {
    lib.getCategories()
      .then((resp) => resp.data)
      .then((planTypes) => planTypes?.data)
      .then(render => setCategories(Array.isArray(render) ? render : []))
  }

  const getItemsDesign = () => {
    setLoading(true);
    myRequest('post', 'itemsDesign', {})
      .then((resp) => resp.data)
      .then((planTypes) => planTypes?.data)
      .then(render => {
        setItemsDesign(render)
      })
      .finally(() => setLoading(false))
  }

  const update = (data = false) => {
    if (!data) {
      notification.warning({ message: 'No Data' });
      return;
    } else {
      let changed = false;
      for (let key of Object.keys(data)) {
        if (data[key] !== CategoryToEditCopy[key]) {
          changed = true;
        }
      }
      if (!changed) {
        notification.warning({ message: 'No Changes was Made' });
        return;
      }
    }

    setLoading(true)
    lib.updateCategory(data || CategoryToEdit)
      .then((resp) => resp.data)
      .then((plan) => {
        if (Number(plan?.error) === 0) {
          notification.success({ message: plan.message, placement: 'topLeft' })
          setVisibleCategoryEdit(false)
        } else {
          notification.error({ message: plan.message, placement: 'topLeft' })
        }
        getCategories()
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    getCategories();
  }, [])

  useEffect(() => {
    // console.log(CategoryToDesign)
    form.setFieldValue('category', CategoryToDesign.plans_categories_id)
    getItemsDesign()
  }, [CategoryToDesign]) //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (visibleCategoryEdit);
    else setCategoryToEdit({});
  }, [visibleCategoryEdit])

  useEffect(() => {
    // console.log(formCreateCategory);
  }, [formCreateCategory])

  const updateCategoryStaus = (item, value) => {
    setLoading(true);
    const data = { plans_categories_status: value, plans_categories_id: item.plans_categories_id };
    update(data);
  }

  const CreateCategory = () => {
    if (keysHaveEmptyValues(formCreateCategory, categoryFields)) {
      notification.warning({ message: 'There are some empty fields, kindly fill then and try again.', placement: 'topLeft' })
      return;
    }
    setLoading(true)
    lib.createCategory(formCreateCategory)
      .then((resp) => resp.data)
      .then((planData) => {
        if (Number(planData?.error) === 0) {
          notification.success({ message: planData.message, placement: 'topLeft' })
          setVisibleCategory(false)
        } else {
          notification.error({ message: planData.message, placement: 'topLeft' })
        }
        // console.log(planData)
        getCategories();

      }).finally(() => setLoading(false))

  }

  const [form] = Form.useForm();

  const onFinish = (values) => {
    console.log('Received values of form:', values);
  };

  const handleChange = (e) => {
    // console.log(e)
    form.setFieldsValue({
      category_items: [],
    });
  };

  const handleItemSelection = e => {
    // console.log(e, items.filter(item => item.plans_items_id != e))
    // setItemsCopy(d => (d.filter(item => item.plans_items_id != e)))
    // console.log(form.getFieldsValue())
  }

  const handleDesignClosed = e => {
    setVisibleCategoryDesign(false)
    form.setFieldsValue({
      items: [],
    });
  }

  const handleDesignSubmit = e => {
    form.validateFields().then(values => {
      setLoading(true)
      setVisibleCategoryDesign(false)
      lib.designCategory(form.getFieldsValue())
        .then(res => res.data)
        .then(des => {
          // console.log(des)
          if (des.error) {
            notification.error({ message: des.message, placement: 'topLeft' })
          } else {
            notification.success({ message: des.message, placement: 'topLeft' })
          }
          form.setFieldValue('items', []);
        });
    })
      .catch(e => {
        notification.error({ message: 'Form Fields Error', placement: 'topLeft' })
      })
      .finally(() => setLoading(false));
  }

  const columnConf = [
    { title: 'Categories Name', dataIndex: 'plans_categories_name', key: 'plans_categories_name', width: '30%' },
    { title: 'Categories Description', dataIndex: 'plans_categories_description', key: 'plans_categories_description', width: '55%' },
    {
      title: 'Status', dataIndex: 'plans_categories_status', key: 'plans_categories_status', render: (_, record) =>
        <>
          {parseInt(_) ? <CheckCircleFilled style={{ fontSize: 25 }} /> : <CloseCircleFilled style={{ color: "#ef4d0e", fontSize: 25 }} />}
          {/* { parseInt(_)? <CheckCircleTwoTone/> : <CloseCircleTwoTone/> } */}
        </>, width: 'auto'
    }
  ];

  const handleRowClick = (record) => {
    setCategoryToEdit(record)
    setCategoryToEditCopy(record)
    setVisibleCategoryEdit(true)
  };

  const rowProps = (record) => {
    return {
      onClick: () => handleRowClick(record)
    };
  };

  return <>
    <DashboardSection
      id="plans-categories"
      icon={<MedicineBoxOutlined />}
      title="Plan categories"
      subtitle={`${(categories || []).length} categor${(categories || []).length === 1 ? 'y' : 'ies'}`}
      collapsible={false}
      variant="content"
      action={(
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setVisibleCategory(true)}>
          New category
        </Button>
      )}
    >
      <div className="plans-table-wrap">
        <Table
          columns={columnConf}
          dataSource={categories}
          onRow={rowProps}
          rootClassName="row"
          rowKey={(record) => String(record?.plans_categories_id)}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${total} categories` }}
        />
      </div>
    </DashboardSection>
    {/* Create Category */}
    <Modal
      {...MODAL_PROPS}
      open={visibleCategory}
      title="Create New Category"
      onOk={null}
      onCancel={() => setVisibleCategory(false)}
      footer={[
        <Button type="primary" danger onClick={() => setVisibleCategory(false)} disabled={loading}>Close</Button>,
        <Button type="primary" onClick={CreateCategory} loading={loading} disabled={loading}>Create</Button>
      ]}
    >
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Row>
            <Col span={8}>Name</Col>
            <Col span={16}>
              <Input type="text" style={{ width: '100%' }} value={formCreateCategory.plans_categories_name} onChange={e => setFormCreateCategory(d => ({ ...d, plans_categories_name: e.target.value }))} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Description</Col>
            <Col span={16}>
              <TextArea style={{ width: '100%' }} value={formCreateCategory.plans_categories_description} onChange={e => setFormCreateCategory(d => ({ ...d, plans_categories_description: e.target.value }))} rows={3} />
            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>

    {/* Design Category */}
    <Modal
      {...MODAL_PROPS}
      open={visibleCategoryDesign}
      title="Design Category"
      onOk={null}
      onCancel={handleDesignClosed}
      footer={[
        <Button type="primary" danger onClick={handleDesignClosed} >Close</Button>,
        <Button type="primary" onClick={handleDesignSubmit} >Save</Button>
      ]}
      style={{ maxHeight: 900 }}
      width={800}
    // destroyOnClose
    >

      <Form
        form={form}
        name="dynamic_form_complex"
        onFinish={onFinish}
        autoComplete="off"
        // onValuesChange={(changed, values) => console.log(changed, values)}
        disabled={loading}
      >
        <Form.Item
          name="category"
          label="Category"
          rules={[
            {
              required: true,
              message: 'Missing area',
            },
          ]}
        >
          <Select onChange={handleChange} disabled={CategoryToDesign?.plans_categories_id}>
            {(Array.isArray(categories) ? categories : []).map(cat => <Option key={cat.plans_categories_id} value={cat.plans_categories_id}>{cat.plans_categories_name}</Option>)}
          </Select>
        </Form.Item>
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <Row key={field.key} style={{ width: '100%' }}>
                  <Col span={18}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, curValues) =>
                        prevValues.area !== curValues.area || prevValues.sights !== curValues.sights
                      }
                      style={{
                        width: '70%',
                      }}
                    >
                      {() => (
                        <Form.Item
                          {...field}
                          label="Item"
                          name={[field.name, 'item']}
                          rules={[
                            {
                              required: true,
                              message: 'Missing Item',
                            },
                          ]}
                          style={{
                            width: '100%',
                          }}
                        >
                          <Select
                            disabled={!form.getFieldValue('category') || loading}
                            onChange={handleItemSelection}
                            style={{
                              width: '100%',
                            }}
                          >
                            {itemsDesign.map((item) => (
                              <Option key={item.plans_items_name} value={item.plans_items_id} disabled={form.getFieldsValue()?.items?.find(itm => itm?.item === item.plans_items_id)}>
                                {item.plans_items_name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Space align="baseline" style={{ width: '100%' }}>
                      <Form.Item
                        {...field}
                        label="Price"
                        name={[field.name, 'price']}
                        rules={[
                          {
                            required: true,
                            message: 'Missing price',
                          },
                        ]}
                        style={{
                          width: '100%',
                        }}
                      >
                        <InputNumber disabled={loading} />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(field.name)} disabled={loading} />
                    </Space>
                  </Col>
                </Row>
              ))}

              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} disabled={!form.getFieldValue('category') || loading}>
                  Add Item
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>

    {/* Edit Category */}
    <Modal
      {...MODAL_PROPS}
      open={visibleCategoryEdit}
      title="Edit Category"
      onOk={null}
      onCancel={() => setVisibleCategoryEdit(false)}
      footer={[
        <Button type="primary" danger onClick={() => setVisibleCategoryEdit(false)} disabled={loading}>Close</Button>,
        <Button disabled={loading} type="primary" onClick={() => updateCategoryStaus(CategoryToEdit, parseInt(CategoryToEdit?.plans_categories_status) ? 0 : 1)} >{parseInt(CategoryToEdit?.plans_categories_status) ? 'Disable' : 'Enable'}</Button>,
        <Button type="primary" onClick={() => update(CategoryToEdit)} loading={loading} disabled={loading}>Save</Button>
      ]}
    >
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Row>
            <Col span={8}>Name</Col>
            <Col span={16}>
              <TextArea disabled={loading} type="text" style={{ width: '100%' }} rows={3} value={CategoryToEdit.plans_categories_name} onChange={e => setCategoryToEdit(d => ({ ...d, plans_categories_name: e.target.value }))} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Description</Col>
            <Col span={16}>
              <TextArea disabled={loading} type="text" style={{ width: '100%' }} rows={5} value={CategoryToEdit.plans_categories_description} onChange={e => setCategoryToEdit(d => ({ ...d, plans_categories_description: e.target.value }))} />
            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>

    {/* Detail Category */}
    <Modal
      {...MODAL_PROPS}
      title={<><strong>{CategoryDetails?.plans_categories_name}</strong> - Detail</>}
      centered
      open={visibleCategoryDetails}
      onOk={() => setVisibleCategoryDetails(false)}
      onCancel={() => setVisibleCategoryDetails(false)}
      closeIcon={loadingDetails ? <LoadingOutlined /> : <CloseOutlined />}
      width={800}
    >
      <Row gap={[12, 24]} justify={'center'}>
        <Col span={24}>
          <Row gap={[8, 18]} >
            <Col span={6}><strong>Catgory Name:</strong></Col>
            <Col span={18}>{CategoryDetails?.plans_categories_name}</Col>
            <Col span={6}><strong>Catgory Description:</strong></Col>
            <Col span={18}>{CategoryDetails?.plans_categories_description}</Col>
          </Row>
        </Col>
        <Divider style={{ padding: 0, margin: '10px 0' }} />
        <Col span={24}>
          <ProgressBar
            height="150"
            width="150"
            ariaLabel="progress-bar-loading"
            wrapperStyle={{ position: 'relative', top: '10%', left: '40%' }}
            wrapperClass="progress-bar-wrapper"
            borderColor='#14442E'
            barColor='#51250F'
            visible={loadingDetails}
          />
          {
            !loadingDetails && visibleCategoryDetails && CategoryDetails?.items.map(item => <Row gap={[12, 12]} key={item?.plans_categories_items_item_id}>
              <Col span={6}><strong>Benefit Name:</strong></Col>
              <Col span={18}>{item?.plans_items_name}</Col>
              <Col span={6}><strong>Benefit Description:</strong></Col>
              <Col span={18}>{item?.plans_items_description}</Col>
              <Col span={6}><strong>Benefit Cost:</strong></Col>
              <Col span={18}>
                <em><CurrencyFormat value={item?.plans_categories_items_cost} displayType={'text'} thousandSeparator={true} prefix={'₦'} /></em>
              </Col>
              <Col span={6}><strong>Benefit Status:</strong></Col>
              <Col span={18}>{parseInt(item?.plans_items_status) ? 'Active' : 'Disabled'}</Col>
              <Col span={24}>&nbsp;</Col>
            </Row>
            )
          }
        </Col>
      </Row>
    </Modal>
  </>
}

export const CreateItems = () => {
  const [p_items, setItems] = useState([]);

  const itemsFields = ['plans_items_name', 'plans_items_description'];

  const [visibleItems, setVisibleItems] = useState(false);
  const [visibleItemsEdit, setVisibleItemsEdit] = useState(false);
  const [ItemsToEdit, setItemsToEdit] = useState({});
  const [ItemsToEditCopy, setItemsToEditCopy] = useState({});
  const [formCreateItems, setFormCreateItems] = useState({});

  const [loading, setLoading] = useState(false);

  const getItems = () => {
    lib.getItems()
      .then((resp) => resp.data)
      .then((planTypes) => planTypes?.data)
      .then(render => setItems(render))
  }

  const update = (data = false) => {
    if (!data) {
      return;
    } else {
      let changed = false;
      for (let key of Object.keys(data)) {
        if (data[key] !== ItemsToEditCopy[key]) {
          changed = true;
        }
      }
      if (!changed) {
        notification.warning({ message: 'No Changes was made' });
        return;
      }
    }

    setLoading(true)
    lib.updateItem(data || ItemsToEdit)
      .then((resp) => resp.data)
      .then((plan) => {
        if (Number(plan?.error) === 0) {
          notification.success({ message: plan.message, placement: 'topLeft' })
          setVisibleItemsEdit(false)
        } else {
          notification.error({ message: plan.message, placement: 'topLeft' })
        }
        getItems()
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    getItems();
  }, [])

  // useEffect(() => {
  //   if(visiblePlan) getPlanTypes();
  //   else setFormCreatePlans({});
  // }, [visiblePlan])

  useEffect(() => {
    if (visibleItemsEdit);
    else setItemsToEdit({});
  }, [visibleItemsEdit])

  useEffect(() => {
    console.log(formCreateItems);
  }, [formCreateItems])

  const updateItemStaus = (item, value) => {
    setLoading(true);
    const data = { plans_items_status: value, plans_items_id: item.plans_items_id }
    update(data);
  }

  const createItem = () => {
    if (keysHaveEmptyValues(formCreateItems, itemsFields)) {
      notification.warning({ message: 'There are some empty fields, kindly fill then and try again.', placement: 'topLeft' })
      return;
    }
    setLoading(true)
    lib.createItem(formCreateItems)
      .then((resp) => resp.data)
      .then((planData) => {
        if (Number(planData?.error) === 0) {
          notification.success({ message: planData.message, placement: 'topLeft' })
          setFormCreateItems([])
        } else {
          notification.error({ message: planData.message, placement: 'topLeft' })
        }
        console.log(planData)
        getItems();
        setVisibleItems(false)

      }).finally(() => setLoading(false))

  }

  const handleRowClick = (record) => {
    setItemsToEdit(record)
    setItemsToEditCopy(record)
    setVisibleItemsEdit(true)
  };

  const rowProps = (record) => {
    return {
      onClick: () => handleRowClick(record)
    };
  };

  const columnConf = [
    { title: 'Items Name', dataIndex: 'plans_items_name', key: 'plans_items_name', width: '30%' },
    { title: 'Items Description', dataIndex: 'plans_items_description', key: 'plans_items_description', width: '55%' },
    {
      title: 'Status', dataIndex: 'plans_items_status', key: 'plans_items_status', render: (_, record) =>
        <>
          {parseInt(_) ? <CheckCircleFilled style={{ fontSize: 25 }} /> : <CloseCircleFilled style={{ color: "#ef4d0e", fontSize: 25 }} />}
        </>, width: 'auto'
    },
  ]

  return <>
    <DashboardSection
      id="plans-items"
      icon={<UnorderedListOutlined />}
      title="Benefit items"
      subtitle={`${(p_items || []).length} item${(p_items || []).length === 1 ? '' : 's'}`}
      collapsible={false}
      variant="content"
      action={(
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setVisibleItems(true)}>
          New item
        </Button>
      )}
    >
      <div className="plans-table-wrap">
        <Table
          columns={columnConf}
          dataSource={p_items}
          onRow={rowProps}
          rootClassName="row"
          rowKey={(record) => String(record?.plans_items_id)}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${total} items` }}
        />
      </div>
    </DashboardSection>

    {/* Create Item */}
    <Modal
      {...MODAL_PROPS}
      open={visibleItems}
      title="Create New Item"
      onOk={null}
      onCancel={() => setVisibleItems(false)}
      footer={[
        <Button type="primary" danger onClick={() => setVisibleItems(false)} disabled={loading}>Close</Button>,
        <Button type="primary" onClick={() => createItem()} loading={loading} disabled={loading}>Ok</Button>
      ]}
    >
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Row>
            <Col span={8}>Name</Col>
            <Col span={16}>
              <Input type="text" style={{ width: '100%' }} value={formCreateItems.plans_items_name} onChange={e => setFormCreateItems(d => ({ ...d, plans_items_name: e.target.value }))} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Description</Col>
            <Col span={16}>
              <TextArea rows={3} type="text" style={{ width: '100%' }} value={formCreateItems.plans_items_description} onChange={e => setFormCreateItems(d => ({ ...d, plans_items_description: e.target.value }))} />
            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>

    <Modal
      {...MODAL_PROPS}
      open={visibleItemsEdit}
      title="Edit Item"
      onOk={null}
      onCancel={() => setVisibleItemsEdit(false)}
      footer={[
        <Button disabled={loading} type="primary" danger onClick={() => setVisibleItemsEdit(false)} >Close</Button>,
        <Button disabled={loading} type="primary" onClick={() => updateItemStaus(ItemsToEdit, parseInt(ItemsToEdit?.plans_items_status) ? 0 : 1)} >{parseInt(ItemsToEdit?.plans_items_status) ? 'Disable' : 'Enable'}</Button>,
        <Button disabled={loading} type="primary" onClick={() => update(ItemsToEdit)} >Save</Button>
      ]}
    >
      <Row gutter={[32, 32]}>
        <Col span={24}>
          <Row>
            <Col span={8}>Name</Col>
            <Col span={16}>
              <TextArea type="text" style={{ width: '100%' }} disabled={loading} value={ItemsToEdit.plans_items_name} onChange={e => setItemsToEdit(d => ({ ...d, plans_items_name: e.target.value }))} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={8}>Description</Col>
            <Col span={16}>
              <TextArea rows={3} type="text" style={{ width: '100%' }} disabled={loading} value={ItemsToEdit.plans_items_description} onChange={e => setItemsToEdit(d => ({ ...d, plans_items_description: e.target.value }))} />
            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>
  </>
}

export default Plans;